import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, TrendUp, Users, Star, ArrowRight, Envelope } from '@phosphor-icons/react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { logEvent } from '../analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PACompanyLanding() {
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/leads`, {
        email,
        company_name: companyName,
        phone,
        source: 'pa_company_landing',
        interested_in: 'featured_listing'
      });
      
      logEvent('Lead', 'Captured', 'PA Company Landing');
      toast.success('Thanks! We\'ll contact you within 24 hours with your free featured listing.');
      setEmail('');
      setCompanyName('');
      setPhone('');
      
      // Redirect to registration after 2 seconds
      setTimeout(() => {
        navigate('/register?role=company&promo=pa-featured');
      }, 2000);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background_light">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-primary text-text_light py-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-block bg-accent text-white px-4 py-2 rounded-sm text-sm uppercase tracking-[0.2em] mb-6">
            🎉 Pennsylvania Pest Control Companies
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tight font-semibold mb-6">
            Get More Customers in Pennsylvania
          </h1>
          <p className="text-2xl leading-relaxed text-secondary mb-8">
            Get <span className="text-accent font-semibold">30 Days FREE Featured Listing</span> on PestPro Hub — Pennsylvania&apos;s #1 Pest Control Directory
          </p>
          <p className="text-xl text-secondary mb-10">
            Join 100+ Pennsylvania pest control companies getting customer leads daily
          </p>
        </div>
      </div>

      {/* Lead Capture Form - Above the Fold */}
      <div className="-mt-10 mb-16 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border-2 border-accent rounded-sm p-8 shadow-lg">
            <h2 className="text-3xl tracking-tight font-medium mb-6 text-center">
              Claim Your Free 30-Day Featured Listing
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  data-testid="lead-company-name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  className="w-full px-4 py-4 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-accent text-lg"
                  placeholder="Your Company Name"
                />
              </div>
              <div>
                <input
                  type="email"
                  data-testid="lead-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-4 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-accent text-lg"
                  placeholder="Your Email Address"
                />
              </div>
              <div>
                <input
                  type="tel"
                  data-testid="lead-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="w-full px-4 py-4 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-accent text-lg"
                  placeholder="Phone Number"
                />
              </div>
              <button
                type="submit"
                data-testid="submit-lead"
                disabled={loading}
                className="w-full bg-accent text-white py-4 rounded-sm hover:bg-accent/90 transition-colors text-lg font-medium disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Get My Free Listing Now →'}
              </button>
            </form>
            <p className="text-center text-sm text-gray-600 mt-4">
              ✅ No credit card required • ✅ Cancel anytime • ✅ Set up in 2 minutes
            </p>
          </div>
        </div>
      </div>

      {/* Social Proof */}
      <div className="bg-accent/10 py-12 px-6 border-y border-accent/30">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-accent mb-2">5,000+</div>
              <div className="text-lg text-gray-700">Monthly Visitors Looking for Pest Control</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-accent mb-2">100+</div>
              <div className="text-lg text-gray-700">PA Companies Already Listed</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-accent mb-2">4.8★</div>
              <div className="text-lg text-gray-700">Average Company Rating</div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-4xl tracking-tight font-semibold text-center mb-12">
          Why Pennsylvania Pest Control Companies Choose PestPro Hub
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white border border-borders rounded-sm p-8">
            <TrendUp size={48} className="text-accent mb-4" weight="duotone" />
            <h3 className="text-2xl font-medium mb-3">Get More Customer Calls</h3>
            <p className="text-base leading-relaxed text-gray-700">
              Featured companies get 10x more visibility. Your listing appears at the top with a green highlight badge when PA residents search for pest control services.
            </p>
          </div>
          
          <div className="bg-white border border-borders rounded-sm p-8">
            <Users size={48} className="text-accent mb-4" weight="duotone" />
            <h3 className="text-2xl font-medium mb-3">Hire Qualified Technicians</h3>
            <p className="text-base leading-relaxed text-gray-700">
              Post unlimited job listings. Access 2,000+ pest control technicians actively looking for work in Pennsylvania. Fill positions faster.
            </p>
          </div>
          
          <div className="bg-white border border-borders rounded-sm p-8">
            <Star size={48} className="text-accent mb-4" weight="duotone" />
            <h3 className="text-2xl font-medium mb-3">Build Trust with Reviews</h3>
            <p className="text-base leading-relaxed text-gray-700">
              Collect and showcase customer reviews. 87% of customers check reviews before calling. Build your reputation online.
            </p>
          </div>
          
          <div className="bg-white border border-borders rounded-sm p-8">
            <CheckCircle size={48} className="text-accent mb-4" weight="duotone" />
            <h3 className="text-2xl font-medium mb-3">Zero Risk - Cancel Anytime</h3>
            <p className="text-base leading-relaxed text-gray-700">
              Try it free for 30 days. After that, just $75/month. No long-term contracts. Cancel anytime with one click. Keep all your reviews.
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="bg-gray-50 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl tracking-tight font-semibold text-center mb-12">
            Simple, Transparent Pricing
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border border-borders rounded-sm p-8">
              <h3 className="text-2xl font-medium mb-4">Basic Listing</h3>
              <div className="text-4xl font-bold mb-6">FREE</div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-green-600 mt-1 flex-shrink-0" />
                  <span>Company profile with services</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-green-600 mt-1 flex-shrink-0" />
                  <span>Contact information displayed</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-green-600 mt-1 flex-shrink-0" />
                  <span>Customer reviews</span>
                </li>
              </ul>
              <Link
                to="/register?role=company"
                className="block text-center bg-gray-200 text-text_dark py-3 rounded-sm hover:bg-gray-300 transition-colors"
              >
                Get Started Free
              </Link>
            </div>
            
            <div className="bg-accent/10 border-2 border-accent rounded-sm p-8 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-accent text-white px-4 py-1 rounded-sm text-sm uppercase tracking-[0.2em]">
                MOST POPULAR
              </div>
              <h3 className="text-2xl font-medium mb-4">Featured Listing</h3>
              <div className="text-4xl font-bold mb-2">
                $75<span className="text-xl">/month</span>
              </div>
              <div className="text-accent font-medium mb-6">First 30 days FREE!</div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-accent mt-1 flex-shrink-0" />
                  <span className="font-medium">Everything in Basic, plus:</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-accent mt-1 flex-shrink-0" />
                  <span>Top placement with green badge</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-accent mt-1 flex-shrink-0" />
                  <span>Show up in &quot;Featured Companies&quot;</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-accent mt-1 flex-shrink-0" />
                  <span>10x more visibility</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-accent mt-1 flex-shrink-0" />
                  <span>Unlimited job postings ($200 value)</span>
                </li>
              </ul>
              <button
                onClick={() => window.scrollTo({ top: 300, behavior: 'smooth' })}
                className="block w-full text-center bg-accent text-white py-3 rounded-sm hover:bg-accent/90 transition-colors font-medium"
              >
                Claim My 30 Days Free
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-4xl tracking-tight font-semibold text-center mb-12">
          What PA Pest Control Companies Say
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white border border-borders rounded-sm p-6">
            <div className="flex gap-1 mb-4">
              {[1,2,3,4,5].map(i => <Star key={i} size={20} weight="fill" className="text-yellow-500" />)}
            </div>
            <p className="text-base leading-relaxed mb-4 italic">
              &quot;We got 3 new customers in the first week. The featured listing pays for itself!&quot;
            </p>
            <p className="font-medium">— Mike&apos;s Pest Control, Pittsburgh</p>
          </div>
          
          <div className="bg-white border border-borders rounded-sm p-6">
            <div className="flex gap-1 mb-4">
              {[1,2,3,4,5].map(i => <Star key={i} size={20} weight="fill" className="text-yellow-500" />)}
            </div>
            <p className="text-base leading-relaxed mb-4 italic">
              &quot;Hired 2 experienced techs through the job board. Super easy platform.&quot;
            </p>
            <p className="font-medium">— PA Pest Solutions, Philadelphia</p>
          </div>
          
          <div className="bg-white border border-borders rounded-sm p-6">
            <div className="flex gap-1 mb-4">
              {[1,2,3,4,5].map(i => <Star key={i} size={20} weight="fill" className="text-yellow-500" />)}
            </div>
            <p className="text-base leading-relaxed mb-4 italic">
              &quot;Finally, a platform made FOR pest control companies. Highly recommend!&quot;
            </p>
            <p className="font-medium">— Harrisburg Exterminators</p>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-primary text-text_light py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl tracking-tight font-semibold mb-6">
            Ready to Grow Your Pennsylvania Pest Control Business?
          </h2>
          <p className="text-xl text-secondary mb-8">
            Join 100+ PA companies getting more customers and qualified technicians
          </p>
          <button
            onClick={() => window.scrollTo({ top: 300, behavior: 'smooth' })}
            className="inline-flex items-center gap-2 bg-accent text-white px-8 py-4 rounded-sm hover:bg-accent/90 transition-colors text-lg font-medium"
          >
            Get Started Free — 30 Days Featured
            <ArrowRight size={24} />
          </button>
          <p className="text-sm text-secondary mt-4">No credit card required • Cancel anytime</p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-borders py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-gray-600">
          <p>© 2026 PestPro Hub • Owned by Raymond T. Jackson, Blue Collar World Pest Control Services</p>
          <p className="text-sm mt-2">Pittsburgh, PA • BU#15680 • <a href="mailto:raymondjackson30@yahoo.com" className="text-accent hover:underline">raymondjackson30@yahoo.com</a></p>
        </div>
      </footer>
    </div>
  );
}
